import { useState, useCallback, useMemo } from 'react';
import { useMousePosition } from '../input/useMousePosition';
import { useOrthoMode } from '../cad/useOrthoMode';
import { useGridSettings } from '../cad/useGridSettings';
import { useOsnapSettings } from '../cad/useOsnapSettings';

/**
 * Composite Hook für CAD-Einstellungen.
 * Kombiniert alle CAD-bezogenen Hooks und stellt eine einheitliche API bereit.
 * Für Rückwärtskompatibilität mit bestehendem Code.
 */
export function useCADSettings() {
  // Sub-Hooks
  const mousePos = useMousePosition();
  const ortho = useOrthoMode(false);
  const grid = useGridSettings(true, 1000);
  const osnapSettings = useOsnapSettings();

  // Letzter gesetzter Punkt (für relative Koordinaten)
  const [lastPoint, setLastPoint] = useState(null);

  // Kombinierte API für Rückwärtskompatibilität
  return useMemo(() => ({
    // State
    orthoMode: ortho.orthoMode,
    gridSnap: grid.gridSnap,
    gridSize: grid.gridSize,
    osnap: osnapSettings.osnap,
    mousePosition: mousePos.mousePosition,
    lastPoint,

    // Setters
    setMousePosition: mousePos.setMousePosition,
    setLastPoint,
    setGridSize: grid.setGridSize,

    // Toggles
    toggleOrtho: ortho.toggleOrtho,
    toggleGridSnap: grid.toggleGridSnap,
    toggleOsnap: osnapSettings.toggleOsnap,

    // Utilities
    getMousePosition: mousePos.getMousePosition,

    // Zusätzliche granulare Kontrolle (optional)
    _mouse: mousePos,
    _ortho: ortho,
    _grid: grid,
    _osnap: osnapSettings,
  }), [
    ortho.orthoMode, ortho.toggleOrtho,
    grid.gridSnap, grid.gridSize, grid.setGridSize, grid.toggleGridSnap,
    osnapSettings.osnap, osnapSettings.toggleOsnap,
    mousePos.mousePosition, mousePos.setMousePosition, mousePos.getMousePosition,
    lastPoint,
  ]);
}

export default useCADSettings;
